import { bool, node } from "prop-types";
import { forwardRef, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import { useUserData } from "Flocknote/apiHooks";

import { useSetLanguage } from "CrossFeatureComponents/MemberToolbar/API/queries";
import { useMemberToobarContext } from "CrossFeatureComponents/MemberToolbar/MemberToolbarState";

import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrDismissibleError from "UI/FnrDismissibleError";
import FnrSelectList2 from "UI/FnrSelectList2";
import FnrTappyShrink from "UI/FnrTappyShrink";
import { marketingColors } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import useOnClickOutside from "Utilities/Hooks/useOnClickOutside";

const hoverGrey = addOpacity(marketingColors.grey, 0.2);

const options = [
  { label: "English", id: "en" },
  { label: "Spanish", id: "es" },
];

const ButtonComponent = forwardRef(({ isLoading, children, ...props }, ref) => {
  const { isDark, isThin } = useMemberToobarContext();
  return (
    <FnrTappyShrink growOnHover>
      <ButtonContainer ref={ref} {...props}>
        <FnrContainedIcon
          icon="fa-globe fas"
          isLoading={isLoading}
          color={isDark ? "textLight" : "textSecondaryDark"}
          size={isThin ? "sm" : "md"}
          yCorrection={isThin ? -0.75 : 0}
        />
      </ButtonContainer>
    </FnrTappyShrink>
  );
});

ButtonComponent.propTypes = { isLoading: bool, children: node };

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  width: 40px;
  align-items: center;
  justify-content: center;
`;

const SetLanguage = (props) => {
  const [{ language }] = useUserData();
  const { isLoading, mutateAsync, errorMessage } = useSetLanguage();
  const [showError, setShowError] = useState(false);
  const containerRef = useRef(null);
  const selectListRef = useRef(null);

  useOnClickOutside(containerRef, () => {
    setShowError(false);
  });

  const handleSelect = async ({ id: newLanguage }) => {
    setShowError(false);
    try {
      await mutateAsync(newLanguage);
      window.location.reload();
    } catch (e) {
      setShowError(true);
    }
  };

  const selectedLanguage = useMemo(
    () => options.find(({ id }) => id === language),
    [language]
  );

  return (
    <SetLanguageContainer ref={containerRef} {...props}>
      <FnrSelectList2
        ButtonComponent={ButtonComponent}
        onSelect={handleSelect}
        disabled={isLoading}
        isLoading={isLoading}
        data-cy="SetLanguage_Input"
        name="language-select"
        selected={selectedLanguage}
        options={options}
        dataCancelClickAway="memberToolbarCancelKey"
        ref={selectListRef}
      >
        {selectedLanguage?.label || "English"}
      </FnrSelectList2>
      <FnrDismissibleError
        translateX={-280}
        xOnRight
        show={showError}
        errorMessage={errorMessage}
        onDismiss={() => setShowError(false)}
      />
    </SetLanguageContainer>
  );
};

const SetLanguageContainer = styled.div`
  /* only show for keyboard users */
  & > button:focus-visible {
    background-color: ${hoverGrey};
  }
  & > button:hover {
    background-color: ${hoverGrey};
  }
  & > button {
    border-radius: 50%;
    transition: background-color 300ms ease-in-out;
  }
  margin: -8px 0px;
  height: 24px;
  text-align: right;
  min-width: 40px;
`;

export { SetLanguage };
